import { Button, EmptyState, ErrorState, RouteProgress } from "@nodus/ui";
import { errorView } from "@nodus/api";
import { createRootRouteWithContext, Outlet, useRouter, useRouterState } from "@tanstack/react-router";

import { lazy, Suspense, useEffect } from "react";

import { consoleMask } from "../app/console-url";

import type { RouterContext } from "../router-context";

const Toaster = lazy(() => import("../app/toaster"));

function Root() {
  const router = useRouter();
  const location = useRouterState({ select: (s) => s.location });
  useEffect(() => {
    if (location.maskedLocation) return;
    const mask = consoleMask(location.href);
    if (mask) void router.navigate({ href: location.href, mask, replace: true, state: (prev) => ({ ...prev, publicConsoleURL: !prev.publicConsoleURL }) });
  }, [location, router]);
  const loading = useRouterState({ select: (s) => s.status === "pending" });
  return (
    <>
      <RouteProgress active={loading} />
      <Outlet />
      <Suspense fallback={null}><Toaster /></Suspense>
    </>
  );
}

function NotFound() {
  return (
    <main className="px-6 py-16">
      <EmptyState title="Page not found" description="This link does not match a console page." actions={<Button onClick={() => window.location.assign("/")}>Go home</Button>} />
    </main>
  );
}

/** A lazy chunk that fails to load after a deploy reloads once, then asks for a reload (ui-spec §4.18). */
function RootError({ error, reset }: { error: unknown; reset: () => void }) {
  const chunk = error instanceof Error && /dynamically imported module|Loading chunk|Importing a module script failed/i.test(error.message);
  if (chunk) {
    try {
      if (!sessionStorage.getItem("nodus.chunkReload")) {
        sessionStorage.setItem("nodus.chunkReload", "1");
        window.location.reload();
        return null;
      }
    } catch {
      // Without storage the page asks instead of reloading automatically.
    }
    return (
      <main className="px-6 py-16">
        <EmptyState title="This page needs a reload" description="A new version of the console is available." actions={<Button onClick={() => window.location.reload()}>Reload</Button>} />
      </main>
    );
  }
  return (
    <main className="mx-auto max-w-2xl px-6 py-16">
      <ErrorState error={errorView(error)} onRetry={reset} />
    </main>
  );
}

export const Route = createRootRouteWithContext<RouterContext>()({
  component: Root,
  notFoundComponent: NotFound,
  errorComponent: RootError,
});
